import React, { useState } from 'react';
import { NavLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import classnames from "classnames";
import OtpInput from 'react-otp-input';
import {
  FORGOT_PASSWORD_REQUEST,
  VERIFY_RESET_CODE_REQUEST,
  RESET_PASSWORD_REQUEST,
} from "../../../actions/auth";
import { Input } from '../../../components/common/Input2'
import EyeIcon from '../../../assets/icons/eye.svg'
import EyeCancelIcon from '../../../assets/icons/eye-cancel.svg'
import logo from '../../../assets/icons/logo.png';
import { ReactComponent as NTIcon } from "../../../assets/icons/nt-logo.svg";
import { ReactComponent as BackIcon } from "../../../assets/icons/chevron-left-sm.svg";
import { useTranslation } from "react-i18next";
import CircularProgress from '@material-ui/core/CircularProgress';
import { Typography, Box } from '@material-ui/core';

const ForgotPassword = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Redux state
  const {
    forgotPasswordLoading,
    forgotPasswordSuccess,
    forgotPasswordError,
    verifyCodeLoading,
    verifyCodeSuccess,
    verifyCodeError,
    resetPasswordLoading,
    resetPasswordSuccess,
    resetPasswordError,
  } = useSelector((state) => state.auth);

  // Component state
  const [step, setStep] = useState(1); // 1: email, 2: code, 3: new password
  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Handle email submission
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setFormErrors({ email: 'Email is required' });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setFormErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setFormErrors({});
    dispatch({
      type: FORGOT_PASSWORD_REQUEST,
      payload: { email: email.trim() }
    });
  };

  // Handle code verification
  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (!resetCode || resetCode.length !== 6) {
      setFormErrors({ code: 'Please enter the 6-digit code' });
      return;
    }

    setFormErrors({});
    dispatch({
      type: VERIFY_RESET_CODE_REQUEST,
      payload: { token: resetCode }
    });
  };

  // Handle password reset
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    const errors = {};

    if (!newPassword) {
      errors.newPassword = 'New password is required';
    } else if (newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters long';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    dispatch({
      type: RESET_PASSWORD_REQUEST,
      payload: { 
        token: resetCode,
        newPassword: newPassword 
      }
    });
  };
  // Handle navigation between steps
  React.useEffect(() => {
    if (forgotPasswordSuccess && step === 1) {
      setStep(2);
    }
    if (verifyCodeSuccess && step === 2) {
      setStep(3);
    }
    if (resetPasswordSuccess && step === 3) {
      // Redirect to login with success message
      navigate('/auth/login', { 
        state: { message: 'Password reset successfully. Please log in with your new password.' }
      });
    }
  }, [forgotPasswordSuccess, verifyCodeSuccess, resetPasswordSuccess, step, navigate]);

  const goBackToStep = (stepNumber) => {
    setStep(stepNumber);
    setFormErrors({});
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className="auth-card">
      <div className="auth-card-head">
        <img src={logo} alt="logo" />
      </div>

      {step === 1 && (
        <form className="auth-card-form" onSubmit={handleEmailSubmit}>
          <Typography variant="h6" style={{ marginBottom: '16px', textAlign: 'center' }}>
            Reset Your Password
          </Typography>
          <Typography variant="body2" style={{ marginBottom: '24px', textAlign: 'center', color: '#666' }}>
            Enter your email address and we'll send you a code to reset your password.
          </Typography>

          <Input
            name="email"
            type="email"
            value={email}
            label="Email Address"
            placeholder="Enter your email"
            onChange={(e) => setEmail(e.target.value)}
            t={t}
          />

          {(formErrors.email || forgotPasswordError) && (
            <span className='auth-failed'>
              {formErrors.email || forgotPasswordError}
            </span>
          )}

          <button 
            className={classnames({ loading: forgotPasswordLoading })} 
            type="submit" 
            disabled={forgotPasswordLoading}
          >
            {forgotPasswordLoading ? <CircularProgress color="inherit" /> : 'Send Reset Code'}
          </button>

          <Box style={{ textAlign: 'center', marginTop: '16px' }}>
            <NavLink to='/auth/login' className='back-link'>
              ← Back to Login
            </NavLink>
          </Box>

          <div className='loginO365-image'>
            <NTIcon />
          </div>
        </form>
      )}

      {step === 2 && (
        <form className="auth-card-form otp" onSubmit={handleCodeSubmit}>
          <div className="otp-header">
            <div className="otp-back" onClick={() => goBackToStep(1)}>
              <BackIcon />
            </div>
            <p>Enter Reset Code</p>
          </div>

          <Typography variant="body2" style={{ marginBottom: '24px', textAlign: 'center', color: '#666' }}>
            We've sent a 6-digit code to {email}. Enter it below to continue.
          </Typography>          <OtpInput
            value={resetCode}
            onChange={setResetCode}
            containerStyle={'otp-inputs'}
            numInputs={6}
            renderSeparator={<span> </span>}
            renderInput={(props) => <input {...props} />}
          />

          {(formErrors.code || verifyCodeError) && (
            <span className='auth-failed'>
              {formErrors.code || verifyCodeError}
            </span>
          )}

          <button 
            className={classnames({ loading: verifyCodeLoading })} 
            type="submit" 
            disabled={verifyCodeLoading || resetCode.length !== 6}
          >
            {verifyCodeLoading ? <CircularProgress color="inherit" /> : 'Verify Code'}
          </button>

          <Box style={{ textAlign: 'center', marginTop: '16px' }}>
            <button 
              type="button"
              className="link-button"
              onClick={() => goBackToStep(1)}
              style={{ background: 'none', border: 'none', color: '#0579df', textDecoration: 'underline', cursor: 'pointer' }}
            >
              Didn't receive the code? Try again
            </button>
          </Box>
        </form>
      )}

      {step === 3 && (
        <form className="auth-card-form" onSubmit={handlePasswordReset}>
          <div className="otp-header">
            <div className="otp-back" onClick={() => goBackToStep(2)}>
              <BackIcon />
            </div>
            <p>Set New Password</p>
          </div>

          <Typography variant="body2" style={{ marginBottom: '24px', textAlign: 'center', color: '#666' }}>
            Enter your new password below.
          </Typography>

          <Input
            name="newPassword"
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            label="New Password"
            placeholder="Enter new password"
            rightIcon={<img src={EyeIcon} alt="" />}
            rightIcon2={<img src={EyeCancelIcon} alt="" />}
            showPassword={() => togglePasswordVisibility()}
            onChange={(e) => setNewPassword(e.target.value)}
            t={t}
          />

          {formErrors.newPassword && (
            <span className='auth-failed'>{formErrors.newPassword}</span>
          )}

          <Input
            name="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            label="Confirm Password"
            placeholder="Confirm new password"
            rightIcon={<img src={EyeIcon} alt="" />}
            rightIcon2={<img src={EyeCancelIcon} alt="" />}
            showPassword={() => toggleConfirmPasswordVisibility()}
            onChange={(e) => setConfirmPassword(e.target.value)}
            t={t}
          />

          {formErrors.confirmPassword && (
            <span className='auth-failed'>{formErrors.confirmPassword}</span>
          )}

          {resetPasswordError && (
            <span className='auth-failed'>{resetPasswordError}</span>
          )}

          <button 
            className={classnames({ loading: resetPasswordLoading })} 
            type="submit" 
            disabled={resetPasswordLoading}
          >
            {resetPasswordLoading ? <CircularProgress color="inherit" /> : 'Reset Password'}
          </button>
        </form>
      )}
    </div>
  );
};

export default ForgotPassword;
